import React, { useCallback, useEffect, useMemo, useState } from "react";

import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Preloader from "../preLoad/preLoader";
import CustomModal from "../modal/customModal";
import ToggleSwitch from "../Switchery/switchery";
import showErrorToast from "../toast/toast";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  const d = new Date(expiryDate);
  const now = new Date();
  return d.getTime() < now.getTime();
};

const getSourceTypeColor = (sourceType) => {
  const t = String(sourceType || "").toLowerCase();
  if (t.includes("purchase")) return "#28a745";
  if (t.includes("recipe")) return "#17a2b8";
  if (t.includes("transfer")) return "#6f42c1";
  if (t.includes("manual") || t.includes("adjust")) return "#fd7e14";
  return "#6c757d";
};

const safeArray = (v) => (Array.isArray(v) ? v : []);

/**
 * View-only stock modal for a variant.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - mode: "parent" | "branch"
 * - parentCompId: number (required for parent mode; used for validation endpoint)
 * - fixedBranchCompId: number | null (branch mode; current branch)
 * - branchOptions: Array<{ value:number, label:string }> (parent mode branch selector)
 * - variant: object (expects menuItem_ID/MenuItem_ID, menuItem_Name/MenuItem_Name, menuItem_DefaultVariantTitle/MenuItem_DefaultVariantTitle, menuItem_IsTrackStock/MenuItem_IsTrackStock)
 * - onToggleTrackStock?: async (checked:boolean) => Promise<void> (optional; if provided, toggle enabled)
 */
const VariantInventoryModal = ({
  isOpen,
  onClose,
  mode = "branch",
  parentCompId,
  fixedBranchCompId = null,
  branchOptions = [],
  variant,
  onToggleTrackStock,
}) => {
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const variantId = useMemo(() => {
    const raw = variant?.menuItem_ID ?? variant?.MenuItem_ID;
    const n = typeof raw === "number" ? raw : parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  }, [variant]);

  const variantName = variant?.menuItem_Name ?? variant?.MenuItem_Name ?? "";
  const variantTitle = variant?.menuItem_DefaultVariantTitle ?? variant?.MenuItem_DefaultVariantTitle ?? "";

  const trackStock = !!(variant?.menuItem_IsTrackStock ?? variant?.MenuItem_IsTrackStock);

  const [selectedBranch, setSelectedBranch] = useState(
    mode === "branch" ? fixedBranchCompId : (branchOptions[0]?.value ?? 0)
  );
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "branch") {
      setSelectedBranch(fixedBranchCompId || 0);
      return;
    }
    // parent mode: default to first branch if current selection invalid
    const valid = safeArray(branchOptions).some((b) => b.value === selectedBranch);
    if (!valid) setSelectedBranch(branchOptions[0]?.value ?? 0);
  }, [isOpen, mode, fixedBranchCompId, branchOptions, selectedBranch]);

  const fetchEntries = useCallback(async () => {
    if (!isOpen) return;
    if (!variantId) return;
    if (!selectedBranch) return;

    try {
      setLoading(true);
      let response;

      if (mode === "parent") {
        response = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_FOR_BRANCH_VARIANT, {
          params: {
            parentCompId,
            branchCompId: selectedBranch,
            menuItemId: variantId,
          },
        });
      } else {
        response = await axiosInstance.get(APIs.GET_INVENTORY_STOCK_BY_MENU_ITEM, {
          params: {
            MenuItem_ID: variantId,
            CompID: selectedBranch,
          },
        });
      }

      const stockData = response.data?.data || response.data?.Data || response.data || [];
      setEntries(safeArray(stockData));
    } catch (err) {
      setEntries([]);
      showErrorToast(err?.response?.data?.message || "Failed to load stock entries.");
    } finally {
      setLoading(false);
    }
  }, [isOpen, mode, parentCompId, selectedBranch, variantId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const totalQty = useMemo(() => {
    return safeArray(entries).reduce((sum, e) => sum + (parseFloat(e.quantity) || 0), 0);
  }, [entries]);

  return (
    <CustomModal
      title="Inventory"
      subtitle="View stock entries"
      isOpen={isOpen}
      onClose={onClose}
      isCentered={true}
      showButton={false}
      modalSize="modal-lg"
      maxHeight="85vh"
    >
      <div style={{ padding: 20 }}>
        {loading && <Preloader />}

        <div
          className="mb-3"
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
            border: `1px solid ${themeColor}20`,
          }}
        >
         
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>
            {variantTitle || "-"}
          </div>
         
        </div>

        <div className="row g-3 mb-3">
          {mode === "parent" && (
            <div className="col-md-6">
              <label className="form-label fw-bold">Branch</label>
              <select
                className="form-select"
                value={selectedBranch || 0}
                onChange={(e) => setSelectedBranch(parseInt(e.target.value, 10))}
                style={{
                  borderRadius: 12,
                  backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                  border: `1px solid ${themeColor}30`,
                  color: isDarkMode ? "#ffffff" : "#000000",
                }}
              >
                {safeArray(branchOptions).length === 0 ? (
                  <option value={0}>No branches</option>
                ) : (
                  safeArray(branchOptions).map((b) => (
                    <option key={b.value} value={b.value}>
                      {b.label}
                    </option>
                  ))
                )}
              </select>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                Stock is managed per branch.
              </div>
            </div>
          )}

          <div className={mode === "parent" ? "col-md-6" : "col-md-12"}>
            <label className="form-label fw-bold">Tracking</label>
            <div
              className="d-flex align-items-center justify-content-between"
              style={{
                padding: 12,
                borderRadius: 12,
                backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                border: `1px solid ${themeColor}20`,
              }}
            >
              <div>
                <div className="fw-bold" style={{ fontSize: 13 }}>
                  Track Stock 
                </div>
              
              </div>
              <ToggleSwitch
                isChecked={trackStock}
                onToggle={(checked) => onToggleTrackStock && onToggleTrackStock(checked)}
                label={trackStock ? "On" : "Off"}
                name={`variant_track_stock_${variantId}`}
                size="small"
                disabled={!onToggleTrackStock}
              />
            </div>
          </div>
        </div>

        <div
          className="mb-3"
          style={{
            padding: 14,
            borderRadius: 14,
            backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
            border: `1px solid ${themeColor}20`,
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="fw-bold" style={{ fontSize: 14 }}>
              Stock Entries
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Total qty: {Number(totalQty || 0).toFixed(2)}
            </div>
          </div>
 
        </div>

        {loading ? null : entries.length === 0 ? (
          <div className="text-center py-4" style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
            <i className="fa fa-inbox me-2" style={{ fontSize: "22px" }}></i>
            <div className="mt-2">No stock entries found</div>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Source</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Container</th>
                  <th>Count</th>
                  <th>Cost</th>
                  <th>Receipt</th>
                  <th>Expiry</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e, idx) => (
                  <tr key={e.inventoryStockId || e.InventoryStockId || idx}>
                    <td>{e.batchNumber || e.BatchNumber || "-"}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: getSourceTypeColor(e.sourceType || e.SourceType),
                          color: "#fff",
                        }}
                      >
                        {e.sourceType || e.SourceType || "-"}
                      </span>
                    </td>
                    <td>{(e.quantity ?? e.Quantity ?? 0).toLocaleString?.("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? (e.quantity ?? e.Quantity ?? 0)}</td>
                    <td>{e.unit || e.Unit || "-"}</td>
                    <td>
                      {e.containerType || e.ContainerType
                        ? (e.containerType || e.ContainerType) !== "Individual" && (e.containerQuantity ?? e.ContainerQuantity) != null && (e.containerQuantity ?? e.ContainerQuantity) > 0
                          ? `${e.containerType || e.ContainerType} (${e.containerQuantity ?? e.ContainerQuantity} units)`
                          : (e.containerType || e.ContainerType)
                        : "-"}
                    </td>
                    <td>
                      {(() => {
                        const qtyNum = parseFloat(e.quantity ?? e.Quantity ?? 0) || 0;
                        const contQty = parseInt(e.containerQuantity ?? e.ContainerQuantity ?? 0, 10) || 0;
                        const numContainers = (e.containerCount != null && e.containerCount !== undefined)
                          ? Number(e.containerCount)
                          : (contQty > 0 && e.containerType && e.containerType !== "Individual")
                            ? qtyNum / contQty
                            : null;
                        if (numContainers == null || numContainers <= 0) return "-";
                        const typeLabel = (e.containerType || e.ContainerType || "").toLowerCase();
                        return typeLabel && typeLabel !== "individual"
                          ? `${numContainers.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${typeLabel}`
                          : `${numContainers.toLocaleString("en-US", { maximumFractionDigits: 2 })} items`;
                      })()}
                    </td>
                    <td>{e.costPrice ?? e.CostPrice ?? "-"}</td>
                    <td>{(e.receiptDate || e.ReceiptDate) ? new Date(e.receiptDate || e.ReceiptDate).toLocaleDateString() : "-"}</td>
                    <td>
                      {(e.expiryDate || e.ExpiryDate) ? (
                        <span
                          style={{
                            color: isExpired(e.expiryDate || e.ExpiryDate) ? "#dc3545" : undefined,
                            fontWeight: isExpired(e.expiryDate || e.ExpiryDate) ? 600 : undefined,
                          }}
                        >
                          {new Date(e.expiryDate || e.ExpiryDate).toLocaleDateString()}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{(e.isActive ?? e.IsActive) ? "Active" : "Inactive"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default VariantInventoryModal;

